/**
 * Braintrust Exporter for Mastra Observability
 *
 * This exporter sends observability data to Braintrust.
 * Root spans become top-level Braintrust spans (no trace wrapper).
 * Events are handled as zero-duration spans with matching start/end times.
 */

import type { AnyExportedSpan, ModelGenerationAttributes, ScoreEvent, SpanErrorInfo } from '@mastra/core/observability';
import { SpanType } from '@mastra/core/observability';
import { omitKeys } from '@mastra/core/utils';
import { TrackingExporter } from '@mastra/observability';
import type { TraceData, TrackingExporterConfig } from '@mastra/observability';
import { initLogger, currentSpan, flush as flushGlobalLogger } from 'braintrust';
import { removeNullish, convertAISDKMessage, serializeToolResult } from './formatter';
import type { OpenAIMessage } from './formatter';
import { formatUsageMetrics } from './metrics';
import { reconstructThreadOutput } from './thread-reconstruction';
import type { ThreadData, ThreadStepData, PendingToolResult } from './thread-reconstruction';

type BraintrustSpanType = 'llm' | 'score' | 'function' | 'eval' | 'task' | 'tool';

/**
 * Explicit span parentage accepted by Braintrust's `startSpan()`.
 * `rootSpanId` groups rows into a trace; `spanId` / `parentSpanIds` populate
 * the row's `span_parents`.
 */
export type BraintrustParentSpanIds =
  | { spanId: string; rootSpanId: string }
  | { parentSpanIds: string[]; rootSpanId: string };

/**
 * The subset of a Braintrust span used by the exporter.
 *
 * This structural interface lets applications provide spans from a different
 * compatible Braintrust SDK instance without coupling Mastra's public types to
 * a specific SDK version.
 */
export interface BraintrustSpan {
  id: string;
  startSpan(args: {
    spanId: string;
    name: string;
    type: BraintrustSpanType;
    startTime: number;
    parentSpanIds?: BraintrustParentSpanIds;
    event: Record<string, any>;
  }): BraintrustSpan;
  log(event: Record<string, any>): void;
  end(args?: { endTime?: number }): number;
  /** Waits for the span's queued rows to reach Braintrust. Present on SDK spans; optional for stand-ins. */
  flush?(): Promise<void>;
}

/**
 * The subset of a Braintrust logger used by the exporter.
 *
 * Logger instances from compatible Braintrust SDK versions satisfy this
 * interface without sharing the same nominal SDK types as the exporter.
 */
export interface BraintrustLogger {
  startSpan(args: {
    spanId: string;
    name: string;
    type: BraintrustSpanType;
    startTime: number;
    parentSpanIds?: BraintrustParentSpanIds;
    event: Record<string, any>;
  }): BraintrustSpan;
  logFeedback(event: {
    id: string;
    scores: Record<string, number>;
    comment?: string;
    metadata?: Record<string, any>;
    source: 'external';
  }): void;
  /** Waits for the logger's queued rows to reach Braintrust. Present on SDK loggers; optional for stand-ins. */
  flush?(): Promise<void>;
}

/**
 * Extended Braintrust span data that includes span type and thread reconstruction data
 */
interface BraintrustSpanData {
  span: BraintrustSpan;
  spanType: SpanType;
  threadData?: ThreadData; // only populated for MODEL_GENERATION spans
  // Tool results stored when TOOL_CALL ends (may arrive before MODEL_STEP ends)
  pendingToolResults?: Map<string, PendingToolResult>; // keyed by toolCallId
}

export interface BraintrustExporterConfig extends TrackingExporterConfig {
  /**
   * Optional Braintrust logger instance.
   * When provided, enables integration with Braintrust contexts such as:
   * - Evals: Agent traces nest inside eval task spans
   * - logger.traced(): Agent traces nest inside traced spans
   * - Parent spans: Auto-detects and attaches to external Braintrust spans
   */
  braintrustLogger?: BraintrustLogger;

  /**
   * Optional resolver for the active Braintrust span.
   *
   * Pass Braintrust's `currentSpan` from the same package instance that creates
   * `Eval()` or `logger.traced()` spans when your app and Mastra may resolve
   * different copies of the `braintrust` package.
   */
  currentSpan?: () => BraintrustSpan | undefined;

  /** Braintrust API key. Required if logger is not provided. */
  apiKey?: string;
  /** Optional custom endpoint */
  endpoint?: string;
  /** Braintrust project name (default: 'mastra-tracing') */
  projectName?: string;
  /** Support tuning parameters */
  tuningParameters?: Record<string, any>;
}

type BraintrustRoot = BraintrustLogger | BraintrustSpan;
type BraintrustTrackedSpan = BraintrustSpanData;
type BraintrustEvent = BraintrustSpan;
type BraintrustMetadata = unknown;
type BraintrustTraceData = TraceData<BraintrustRoot, BraintrustTrackedSpan, BraintrustEvent, BraintrustMetadata>;

// Default span type for all spans
const DEFAULT_SPAN_TYPE = 'task';

// Exceptions to the default mapping
const SPAN_TYPE_EXCEPTIONS: Partial<Record<SpanType, string>> = {
  [SpanType.MODEL_GENERATION]: 'llm',
  [SpanType.TOOL_CALL]: 'tool',
  [SpanType.MCP_TOOL_CALL]: 'tool',
  [SpanType.PROVIDER_TOOL_CALL]: 'tool',
  [SpanType.WORKFLOW_CONDITIONAL_EVAL]: 'function',
  [SpanType.WORKFLOW_WAIT_EVENT]: 'function',
};

// Mapping function - returns valid Braintrust span types
function mapSpanType(spanType: SpanType): 'llm' | 'score' | 'function' | 'eval' | 'task' | 'tool' {
  return (SPAN_TYPE_EXCEPTIONS[spanType] as any) ?? DEFAULT_SPAN_TYPE;
}

export class BraintrustExporter extends TrackingExporter<
  BraintrustRoot,
  BraintrustTrackedSpan,
  BraintrustEvent,
  BraintrustMetadata,
  BraintrustExporterConfig
> {
  name = 'braintrust';

  // Flags and logger for context-aware mode
  #useProvidedLogger: boolean;
  #providedLogger?: BraintrustLogger;
  #localLogger?: BraintrustLogger;
  // Latest ambient span Mastra attached to. Its rows go through that span's own
  // SDK state, not through the exporter's logger. One slot is enough: every span
  // from one SDK copy shares the same background logger.
  #externalSpan?: BraintrustSpan;

  constructor(config: BraintrustExporterConfig = {}) {
    // Resolve env vars BEFORE calling super (config is readonly in base class)
    const resolvedApiKey = config.apiKey ?? process.env.BRAINTRUST_API_KEY;
    const resolvedEndpoint = config.endpoint ?? process.env.BRAINTRUST_ENDPOINT;

    super({
      ...config,
      apiKey: resolvedApiKey,
      endpoint: resolvedEndpoint,
    });

    this.#useProvidedLogger = !!config.braintrustLogger;

    if (this.#useProvidedLogger) {
      // Use provided logger - enables Braintrust context integration
      this.#providedLogger = config.braintrustLogger;
    } else {
      // Validate apiKey for creating loggers per trace
      if (!this.config.apiKey) {
        this.setDisabled(
          `Missing required API key. Set BRAINTRUST_API_KEY environment variable or pass apiKey in config.`,
        );
        return;
      }
      // lazy create logger on first rootSpan
      this.#localLogger = undefined;
    }
  }

  private async getLocalLogger(): Promise<BraintrustLogger | undefined> {
    if (this.#localLogger) {
      return this.#localLogger;
    }
    try {
      const logger = await initLogger({
        projectName: this.config.projectName ?? 'mastra-tracing',
        apiKey: this.config.apiKey,
        appUrl: this.config.endpoint,
        ...this.config.tuningParameters,
      });
      this.#localLogger = logger;
      return logger;
    } catch (err) {
      this.logger.error('Braintrust exporter: Failed to initialize logger', { error: err });
      this.setDisabled('Failed to initialize Braintrust logger');
    }
  }

  async onScoreEvent(event: ScoreEvent): Promise<void> {
    if (this.isDisabled) return;

    const { score } = event;
    const rowId = score.spanId ?? score.traceId;
    if (!rowId) {
      this.logger.debug('Braintrust exporter: skipping score with no spanId or traceId', {
        scorerId: score.scorerId,
      });
      return;
    }

    const logger = this.#useProvidedLogger ? this.#providedLogger : await this.getLocalLogger();
    if (!logger) return;

    const name = score.scorerName ?? score.scorerId;

    try {
      logger.logFeedback({
        id: rowId,
        scores: { [name]: score.score },
        ...(score.reason ? { comment: score.reason } : {}),
        metadata: {
          scorerId: score.scorerId,
          ...(score.scoreSource ? { scoreSource: score.scoreSource } : {}),
          ...(score.metadata ?? {}),
        },
        source: 'external',
      });
    } catch (err) {
      this.logger.error('Braintrust exporter: Failed to submit score', {
        error: err,
        traceId: score.traceId,
        spanId: score.spanId,
        scorerId: score.scorerId,
      });
    }
  }

  private startSpan(args: {
    parent: BraintrustSpan | BraintrustLogger;
    span: AnyExportedSpan;
    parentSpanIds?: BraintrustParentSpanIds;
  }): BraintrustSpanData {
    const { parent, span, parentSpanIds } = args;
    const payload = this.buildSpanPayload(span);

    // Braintrust's startSpan() accepts data properties via the `event` parameter
    // which maps to StartSpanEventArgs (ExperimentLogPartialArgs & Partial<IdField>)
    // This includes: input, output, metadata, metrics, tags, scores, error, etc.
    const braintrustSpan = parent.startSpan({
      spanId: span.id,
      name: span.name,
      type: mapSpanType(span.type),
      startTime: span.startTime.getTime() / 1000,
      ...(parentSpanIds ? { parentSpanIds } : {}),
      event: {
        id: span.id, // Use Mastra span ID as Braintrust row ID for logFeedback() compatibility
        ...payload,
      },
    });

    // Create BraintrustSpanData with span type for tree walking
    // Initialize threadData and pendingToolResults for MODEL_GENERATION spans (used for Thread view reconstruction)
    const isModelGeneration = span.type === SpanType.MODEL_GENERATION;
    return {
      span: braintrustSpan,
      spanType: span.type,
      threadData: isModelGeneration ? [] : undefined,
      pendingToolResults: isModelGeneration ? new Map() : undefined,
    };
  }

  /**
   * Explicit parentage for a root span started directly from a logger.
   *
   * Pinning `rootSpanId` to the Mastra trace ID makes every root sharing a
   * trace land in the same Braintrust trace — required for a resumed workflow
   * run to rejoin the trace of its suspended half, which was exported by an
   * earlier process. Genuine roots keep empty `span_parents` so Braintrust
   * still treats them as trace roots; only a resumed continuation (marked by
   * core with `resumedFromSpanId`) or a run the caller nested with
   * `tracingOptions.nestUnderParent` links to its parent span. Roots
   * with other parent IDs (e.g. an ambient OTEL span from the bridge) are not
   * linked — their parent was never exported to Braintrust.
   *
   * Spans nested under an enclosing Braintrust span (Eval(), logger.traced(),
   * or a span passed as `braintrustLogger`) get no explicit parentage: the
   * startSpan() chain already inherits the external trace's rootSpanId.
   */
  private rootParentSpanIds(root: BraintrustRoot, span: AnyExportedSpan): BraintrustParentSpanIds | undefined {
    // Braintrust SDK objects carry a `kind` discriminant ('logger' | 'span').
    // Structural stand-ins may omit it; for those, spans are the ones that end().
    const kind = (root as { kind?: string }).kind;
    const isLoggerRoot = kind ? kind === 'logger' : !('end' in root);
    if (!isLoggerRoot) {
      return undefined;
    }
    if (span.parentSpanId && span.parentSpanId === span.metadata?.resumedFromSpanId) {
      return { spanId: span.parentSpanId, rootSpanId: span.traceId };
    }
    // The caller declared the parent to be a span of this trace
    // (tracingOptions.nestUnderParent), so link to it by id as well.
    if (span.nestedUnderParent && span.externalParentSpanId) {
      return { spanId: span.externalParentSpanId, rootSpanId: span.traceId };
    }
    return { parentSpanIds: [], rootSpanId: span.traceId };
  }

  protected override async _buildRoot(_args: {
    span: AnyExportedSpan;
    traceData: BraintrustTraceData;
  }): Promise<BraintrustRoot | undefined> {
    if (this.#useProvidedLogger) {
      // Try to find a Braintrust span to attach to:
      // 1. Auto-detect from Braintrust's current span (logger.traced(), Eval(), etc.)
      // 2. Fall back to the configured logger
      let externalSpan: BraintrustSpan | undefined;
      try {
        externalSpan = this.config.currentSpan?.();
      } catch (err) {
        this.logger.error('Braintrust exporter: Failed to resolve configured currentSpan', { error: err });
      }
      externalSpan ??= currentSpan();

      // Check if it's a valid span (not the NOOP_SPAN)
      if (externalSpan && externalSpan.id) {
        // External span detected - attach Mastra traces to it
        this.#externalSpan = externalSpan;
        return externalSpan;
      } else {
        // No external span - use provided logger
        return this.#providedLogger!;
      }
    } else {
      // Use the local logger
      return this.getLocalLogger();
    }
  }

  protected override async _buildSpan(args: {
    span: AnyExportedSpan;
    traceData: BraintrustTraceData;
  }): Promise<BraintrustSpanData | undefined> {
    const { span, traceData } = args;

    if (span.isRootSpan) {
      const root = traceData.getRoot();
      if (root) {
        return this.startSpan({ parent: root, span, parentSpanIds: this.rootParentSpanIds(root, span) });
      }
    } else {
      const parent = traceData.getParent(args);
      if (parent) {
        // Parent could be BraintrustSpanData (has .span) or BraintrustRoot (Logger/Span, no .span)
        const parentSpan = 'span' in parent ? parent.span : parent;
        return this.startSpan({ parent: parentSpan, span });
      }
    }
  }

  protected override async _buildEvent(args: {
    span: AnyExportedSpan;
    traceData: BraintrustTraceData;
  }): Promise<BraintrustEvent | undefined> {
    const spanData = await this._buildSpan(args);

    if (!spanData) {
      // parent doesn't exist and not creating rootSpan, return early data
      return;
    }

    spanData.span.end({ endTime: args.span.startTime.getTime() / 1000 });
    return spanData.span;
  }

  protected override async _updateSpan(args: { span: AnyExportedSpan; traceData: BraintrustTraceData }): Promise<void> {
    const { span, traceData } = args;

    const spanData = traceData.getSpan({ spanId: span.id });
    if (!spanData) {
      return;
    }
    spanData.span.log(this.buildSpanPayload(span, false));
  }

  protected override async _finishSpan(args: { span: AnyExportedSpan; traceData: BraintrustTraceData }): Promise<void> {
    const { span, traceData } = args;

    const spanData = traceData.getSpan({ spanId: span.id });
    if (!spanData) {
      return;
    }

    // Handle thread data accumulation for MODEL_STEP and tool spans.
    // PROVIDER_TOOL_CALL is excluded: provider results are not merged into the
    // reconstructed thread output. Since @mastra/core parents these spans under
    // the delivering MODEL_STEP, findModelGenerationAncestor would now succeed,
    // so accumulating them is possible as a follow-up.
    if (span.type === SpanType.MODEL_STEP) {
      this.accumulateModelStepData(span, traceData);
    } else if (span.type === SpanType.TOOL_CALL || span.type === SpanType.MCP_TOOL_CALL) {
      this.accumulateToolCallResult(span, traceData);
    }

    // Build payload - for MODEL_GENERATION, may reconstruct output from threadData
    const payload =
      span.type === SpanType.MODEL_GENERATION
        ? this.buildModelGenerationPayload(span, spanData)
        : this.buildSpanPayload(span, false);

    spanData.span.log(payload);

    if (span.endTime) {
      spanData.span.end({ endTime: span.endTime.getTime() / 1000 });
    } else {
      spanData.span.end();
    }
  }

  protected override async _abortSpan(args: { span: BraintrustTrackedSpan; reason: SpanErrorInfo }): Promise<void> {
    const { span: spanData, reason } = args;
    spanData.span.log({
      error: reason.message,
      metadata: { errorDetails: reason },
    });
    spanData.span.end();
  }

  /**
   * Wait for Braintrust's background logger to send queued rows.
   *
   * `span.log()`, `span.end()` and `logger.logFeedback()` only enqueue; the SDK
   * sends on its own schedule. Rows attached to an ambient span are flushed via
   * that span, which targets the SDK copy that created it — the package-level
   * `flush()` only reaches the copy this exporter imports.
   */
  private async flushPending(): Promise<void> {
    const logger = this.#useProvidedLogger ? this.#providedLogger : this.#localLogger;
    const pending: Promise<void>[] = [];
    if (logger?.flush) {
      pending.push(logger.flush());
    }
    if (this.#externalSpan) {
      pending.push(this.#externalSpan.flush ? this.#externalSpan.flush() : flushGlobalLogger());
    }
    await Promise.all(pending);
  }

  protected override async _flush(): Promise<void> {
    await this.flushPending();
  }

  // Runs after shutdown() has written abort rows for still-open spans.
  protected override async _postShutdown(): Promise<void> {
    await this.flushPending();
  }

  // ==============================================================================
  // Thread view reconstruction helpers
  // ==============================================================================

  /**
   * Walk up the tree to find the MODEL_GENERATION ancestor span.
   * Returns the BraintrustSpanData if found, undefined otherwise.
   */
  private findModelGenerationAncestor(spanId: string, traceData: BraintrustTraceData): BraintrustSpanData | undefined {
    let currentId: string | undefined = spanId;

    while (currentId) {
      const parentId = traceData.getParentId({ spanId: currentId });
      if (!parentId) return undefined;

      const parentSpanData = traceData.getSpan({ spanId: parentId });
      if (parentSpanData?.spanType === SpanType.MODEL_GENERATION) {
        return parentSpanData;
      }
      currentId = parentId;
    }

    return undefined;
  }

  /**
   * Accumulate MODEL_STEP data to the parent MODEL_GENERATION's threadData.
   * Called when a MODEL_STEP span ends.
   */
  private accumulateModelStepData(span: AnyExportedSpan, traceData: BraintrustTraceData): void {
    const modelGenSpanData = this.findModelGenerationAncestor(span.id, traceData);
    if (!modelGenSpanData?.threadData) {
      return;
    }

    // Extract step data from MODEL_STEP output and attributes
    const output = span.output as
      | { text?: string; toolCalls?: Array<{ toolCallId: string; toolName: string; args: unknown }> }
      | undefined;
    const attributes = span.attributes as { stepIndex?: number } | undefined;

    const stepData: ThreadStepData = {
      stepSpanId: span.id,
      stepIndex: attributes?.stepIndex ?? 0,
      text: output?.text,
      toolCalls: output?.toolCalls?.map(tc => ({
        toolCallId: tc.toolCallId,
        toolName: tc.toolName,
        args: tc.args,
      })),
    };

    modelGenSpanData.threadData.push(stepData);
  }

  /**
   * Store a tool result (TOOL_CALL or MCP_TOOL_CALL) in parent MODEL_GENERATION's pendingToolResults.
   * Called when the tool span ends.
   * Results are merged into threadData when MODEL_GENERATION ends.
   */
  private accumulateToolCallResult(span: AnyExportedSpan, traceData: BraintrustTraceData): void {
    const modelGenSpanData = this.findModelGenerationAncestor(span.id, traceData);
    if (!modelGenSpanData?.pendingToolResults) {
      return;
    }

    const toolCallId = this.resolveToolCallId(span);

    // Store the result for later merging
    modelGenSpanData.pendingToolResults.set(toolCallId, {
      result: span.output,
      startTime: span.startTime,
    });
  }

  /**
   * Build the payload for MODEL_GENERATION span, reconstructing output from threadData if available.
   */
  private buildModelGenerationPayload(span: AnyExportedSpan, spanData: BraintrustSpanData): Record<string, any> {
    const basePayload = this.buildSpanPayload(span, false);

    // Check if we have threadData with tool calls to reconstruct
    const threadData = spanData.threadData;
    if (!threadData || threadData.length === 0) {
      return basePayload;
    }

    // Merge pending tool results into threadData
    if (spanData.pendingToolResults && spanData.pendingToolResults.size > 0) {
      for (const step of threadData) {
        if (step.toolCalls) {
          for (const toolCall of step.toolCalls) {
            const pendingResult = spanData.pendingToolResults.get(toolCall.toolCallId);
            if (pendingResult) {
              toolCall.result = pendingResult.result;
              toolCall.startTime = pendingResult.startTime;
            }
          }
        }
      }
    }

    // Check if any step has tool calls
    const hasToolCalls = threadData.some(step => step.toolCalls && step.toolCalls.length > 0);
    if (!hasToolCalls) {
      return basePayload;
    }

    // Reconstruct output as OpenAI messages
    const reconstructedOutput = reconstructThreadOutput(threadData, span.output);
    return {
      ...basePayload,
      output: reconstructedOutput,
    };
  }

  /**
   * Transforms MODEL_GENERATION input to Braintrust Thread view format.
   * Converts AI SDK messages (v4/v5) to OpenAI Chat Completion format, which Braintrust requires
   * for proper rendering of threads (fixes #11023).
   */
  private transformInput(input: unknown, spanType: SpanType): unknown {
    if (spanType === SpanType.MODEL_GENERATION) {
      // If input is already an array of messages, convert AI SDK format to OpenAI format
      if (Array.isArray(input)) {
        return input.map((msg: unknown) => convertAISDKMessage(msg));
      }

      // If input has a messages array
      if (
        input &&
        typeof input === 'object' &&
        'messages' in input &&
        Array.isArray((input as { messages: unknown[] }).messages)
      ) {
        return (input as { messages: unknown[] }).messages.map((msg: unknown) => convertAISDKMessage(msg));
      }
    }

    return input;
  }

  /**
   * Transforms MODEL_GENERATION output to Braintrust Thread view format.
   */
  private transformOutput(output: any, spanType: SpanType): any {
    if (spanType === SpanType.MODEL_GENERATION) {
      if (!output || typeof output !== 'object') {
        return output;
      }
      const { text, ...rest } = output;
      // Remove null/undefined values from rest to keep Thread view clean
      return { role: 'assistant', content: text, ...removeNullish(rest) };
    }

    return output;
  }

  private isToolSpan(span: AnyExportedSpan): boolean {
    return (
      span.type === SpanType.TOOL_CALL ||
      span.type === SpanType.MCP_TOOL_CALL ||
      span.type === SpanType.PROVIDER_TOOL_CALL
    );
  }

  private getToolName(span: AnyExportedSpan): string {
    if (span.entityName) {
      return span.entityName;
    }
    const match = /'([^']+)'/.exec(span.name);
    return match?.[1] ?? span.name;
  }

  private resolveToolCallId(span: AnyExportedSpan): string {
    const attrs = span.attributes as { toolCallId?: string } | undefined;
    return (
      attrs?.toolCallId ??
      span.metadata?.toolCallId ??
      (span.input as { toolCallId?: string } | undefined)?.toolCallId ??
      span.id
    );
  }

  private toToolCallInput(span: AnyExportedSpan): OpenAIMessage[] {
    const args = span.input;
    let argsString: string;
    if (typeof args === 'string') {
      argsString = args;
    } else {
      try {
        argsString = JSON.stringify(args ?? {});
      } catch {
        argsString = '[unserializable result]';
      }
    }
    return [
      {
        role: 'assistant',
        content: '',
        tool_calls: [
          {
            id: this.resolveToolCallId(span),
            type: 'function',
            function: {
              name: this.getToolName(span),
              arguments: argsString,
            },
          },
        ],
      },
    ];
  }

  private toToolCallOutput(span: AnyExportedSpan): OpenAIMessage {
    return {
      role: 'tool',
      content: serializeToolResult(span.output),
      tool_call_id: this.resolveToolCallId(span),
    };
  }

  private buildSpanPayload(span: AnyExportedSpan, isCreate = true): Record<string, any> {
    const payload: Record<string, any> = {};

    const isToolType = this.isToolSpan(span);

    if (span.input !== undefined) {
      payload.input = isToolType ? this.toToolCallInput(span) : this.transformInput(span.input, span.type);
    }

    if (span.output !== undefined) {
      payload.output = isToolType ? this.toToolCallOutput(span) : this.transformOutput(span.output, span.type);
    }

    if (isCreate && span.isRootSpan && span.tags?.length) {
      payload.tags = span.tags;
    }

    // Initialize metrics and metadata objects
    payload.metrics = {};
    // Spread span.metadata first, then set spanType to prevent accidental override
    payload.metadata = {
      ...span.metadata,
      spanType: span.type,
    };

    if (isCreate) {
      payload.metadata['mastra-trace-id'] = span.traceId;
    }

    const attributes = (span.attributes ?? {}) as Record<string, any>;

    if (span.type === SpanType.MODEL_GENERATION) {
      const modelAttr = attributes as ModelGenerationAttributes;

      // Model goes to metadata
      if (modelAttr.model !== undefined) {
        payload.metadata.model = modelAttr.model;
      }

      // Provider goes to metadata (if provided by attributes)
      if (modelAttr.provider !== undefined) {
        payload.metadata.provider = modelAttr.provider;
      }

      // Prefer resolved model ID (e.g. "claude-sonnet-4-5-20250929") over
      // gateway aliases (e.g. "claude-sonnet-4.5") for accurate cost estimation
      if (modelAttr.responseModel !== undefined) {
        payload.metadata.model = modelAttr.responseModel;
      }

      // Usage/token info goes to metrics
      payload.metrics = formatUsageMetrics(modelAttr.usage);

      // Time to first token (TTFT) for streaming responses
      // Braintrust expects TTFT in seconds (not milliseconds)
      if (modelAttr.completionStartTime) {
        payload.metrics.time_to_first_token =
          (modelAttr.completionStartTime.getTime() - span.startTime.getTime()) / 1000;
      }

      // Model parameters go to metadata
      if (modelAttr.parameters !== undefined) {
        payload.metadata.modelParameters = modelAttr.parameters;
      }

      // Other LLM attributes go to metadata
      const otherAttributes = omitKeys(attributes, [
        'model',
        'responseModel',
        'usage',
        'parameters',
        'completionStartTime',
      ]);
      payload.metadata = {
        ...payload.metadata,
        ...otherAttributes,
      };
    } else {
      // For non-LLM spans, put all attributes in metadata
      payload.metadata = {
        ...payload.metadata,
        ...attributes,
      };
    }

    // Handle errors
    if (span.errorInfo) {
      payload.error = span.errorInfo.message;
      payload.metadata.errorDetails = span.errorInfo;
    }

    // Clean up empty metrics object
    if (Object.keys(payload.metrics).length === 0) {
      delete payload.metrics;
    }

    // Remove null/undefined values from metadata to keep Braintrust UI clean
    payload.metadata = removeNullish(payload.metadata);

    return payload;
  }
}
